/**
 * Endpoint #19 — the live-update stream (ADR-004, §5.5, §12.2).
 *
 * One EventSource per tab. Events carry minimal payloads; this module translates them into
 * TanStack Query invalidations, so every screen re-reads through the normal GETs and a dropped
 * stream degrades to refetch-on-focus, never to wrong data. Native reconnect is used as-is
 * (`retry:` hint from the server); on every reconnect the client resyncs `/api/profiles` and
 * `/api/status` (§5.5).
 */

import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { SseEventMap, SseEventName } from '@lw/shared';
import { apiUrl } from './api/client';
import { QK } from './api/queryKeys';
import { useLatest } from './lib/hooks';

export const EVENTS_URL = apiUrl('/events');

/**
 * Screens that need the *event*, not just fresh data.
 *
 * Cache invalidation is still the rule (§12.2) — this is the exception for a surface that must
 * deliberately **not** refresh itself: the global feed arms a quiet "New posts" pill instead, so
 * the list never shifts under the reader. Listeners are notified after the invalidation map has
 * run, and a listener that throws cannot break the stream.
 */
type SseListener = (name: SseEventName, data: SseEventMap[SseEventName]) => void;
const listeners = new Set<SseListener>();

function broadcast<K extends SseEventName>(name: K, data: SseEventMap[K]): void {
  for (const listener of listeners) {
    try {
      listener(name, data);
    } catch {
      /* a screen's handler is never allowed to take the stream down */
    }
  }
}

/** Subscribes to one event name for the lifetime of the component. */
export function useSseEvent<K extends SseEventName>(
  name: K,
  handler: (data: SseEventMap[K]) => void,
): void {
  const latest = useLatest(handler);
  useEffect(() => {
    const listener: SseListener = (eventName, data) => {
      if (eventName === name) latest.current(data as SseEventMap[K]);
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [name, latest]);
}

function parseEventData<T>(raw: unknown): T | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * Opens the stream and wires the §12.2 invalidation map. Returns the disposer.
 * Mounted once, inside the authenticated shell — an unauthenticated stream would just 401-loop.
 */
export function connectServerEvents(queryClient: QueryClient): () => void {
  const source = new EventSource(EVENTS_URL, { withCredentials: true });
  const invalidate = (queryKey: readonly unknown[]): void => {
    void queryClient.invalidateQueries({ queryKey });
  };

  const on = <K extends SseEventName>(name: K, handle: (data: SseEventMap[K]) => void): void => {
    source.addEventListener(name, (event: Event) => {
      const data = parseEventData<SseEventMap[K]>((event as MessageEvent<unknown>).data);
      if (data === null) return;
      handle(data);
      broadcast(name, data);
    });
  };

  let connected = false;
  source.addEventListener('open', () => {
    // First open is the initial connection; every later one is a reconnect and needs a resync.
    if (connected) {
      invalidate(QK.profiles);
      invalidate(QK.status);
    }
    connected = true;
  });

  on('run.updated', (data) => {
    invalidate(QK.profiles);
    invalidate(QK.runs(data.profileId));
  });

  on('profile.status', () => {
    invalidate(QK.profiles);
  });

  on('status.changed', () => {
    invalidate(QK.status);
    invalidate(QK.views);
  });

  on('job.progress', () => {
    invalidate(QK.jobs);
  });

  // An insert and an absorb both change what the strip shows, and both arrive here: the client
  // re-reads #48 rather than patching, so the ordering stays the server's.
  on('notification.fired', () => {
    invalidate(QK.notifications);
  });

  // A watch check found a fresh post: the `/watch` feed grows at its head and the settings tab's
  // "detections today" figure moves. Both are server-ordered reads, so both just re-read.
  on('watch.detection', () => {
    invalidate(QK.watchFeed);
    invalidate(QK.watch);
  });

  return () => {
    source.close();
  };
}

/** Mount point for the stream — used once by the authenticated shell. */
export function useServerEvents(): void {
  const queryClient = useQueryClient();
  useEffect(() => connectServerEvents(queryClient), [queryClient]);
}
